/**
 * MasterGanttToolbar.tsx
 * Phase 4, Item 5 — Toolbar for the Master Gantt view.
 * Follow-up 1.5 — Bulk action bar (Commit Selected / Export Selected CSV).
 *
 * Controls (left → right):
 *   - "Master View" title
 *   - Date range pickers (Start / End) + Apply button + Refresh icon
 *   - Filter dropdowns: Lab Section / Machine / Work Type / Clear
 *   - Spacer
 *   - Toggle Proposals switch (show/hide Tentative bars)
 *   - Multi-select toggle button (with selected-count chip)
 *   - When multiSelectOn && selectedCount > 0: MasterGanttBulkActions bar
 *   - Global Export split button (PNG / PDF / CSV)
 *
 * The toolbar is presentation-only. All state is managed by the parent
 * (MasterGanttPage) via hooks and passed in as props.
 *
 * Highcharts export (PNG, PDF): calls `chartRef.current.chart.exportChart()`
 * after dynamically importing the exporting + offline-exporting modules.
 * CSV: calls the provided `onExportCsv` callback.
 */

import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Checklist,
  FileDownload,
  ImageOutlined,
  PictureAsPdfOutlined,
  PrecisionManufacturing,
  Refresh,
  TableChartOutlined,
  ViewListOutlined,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { MachineScheduleFilters } from './MachineScheduleFilters.js';
import type { MachineSummaryForFilter } from './MachineScheduleFilters.js';
import type { MasterGanttFilterState } from './useMasterGanttFilters.js';
import { MasterGanttBulkActions } from './MasterGanttBulkActions.js';
import type { MasterGanttResponse } from '../../../types/schedule.js';
import type { GanttGroupBy } from '../../../components/schedule/masterGanttRowBuilder.js';
import { SCHEDULE_RANGE_PRESETS, matchRangePreset } from './dateRangePresets.js';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface MasterGanttToolbarProps {
  // ── Date range ────────────────────────────────────────────────────────────
  startDate: string;
  endDate: string;
  onStartDateChange: (val: string) => void;
  onEndDateChange: (val: string) => void;
  onApply: () => void;
  onRefresh: () => void;
  loading: boolean;
  /** Quick-range preset selected → set both dates and apply (see dateRangePresets). */
  onRangePreset: (startDate: string, endDate: string) => void;

  // ── Group by (REQ-03) ──────────────────────────────────────────────────────
  /** Top-level grouping of the Gantt: 'project' (default) or 'machine'. */
  groupBy: GanttGroupBy;
  /** Called when the user flips the Group-by toggle. */
  onGroupByChange: (groupBy: GanttGroupBy) => void;

  // ── Filters ───────────────────────────────────────────────────────────────
  filterState: MasterGanttFilterState;
  machines: MachineSummaryForFilter[];
  hasActiveFilters: boolean;
  onFilterChange: (patch: Partial<MasterGanttFilterState>) => void;
  onClearFilters: () => void;
  // bookingsInWindow removed (FEAT-2026-0007 6E.1): Quote/WO dropdowns no
  // longer live in MachineScheduleFilters; Master Gantt quoteKey filtering
  // runs via useMasterGanttFilters + server-side/applyMasterGanttQuoteWorkOrderFilter.

  // ── Toggle Proposals ─────────────────────────────────────────────────────
  // Exposed via filterState.showProposals + onFilterChange({ showProposals })

  // ── Multi-select ──────────────────────────────────────────────────────────
  multiSelectOn: boolean;
  selectedCount: number;
  selectedKeys: Set<string>;
  onMultiSelectToggle: (on: boolean) => void;

  // ── Bulk actions (Follow-up 1.5) ──────────────────────────────────────────
  /** Full gantt response, passed to MasterGanttBulkActions for CSV + key splitting. */
  ganttData: MasterGanttResponse | null;
  /** Called after a successful bulk commit; parent should refresh + clear selection. */
  onBulkCommitSuccess: () => void;
  /** Called when bulk action fails; parent surfaces error banner. */
  onBulkError: (message: string) => void;

  // ── Export ────────────────────────────────────────────────────────────────
  /** Called by the toolbar when user picks PNG or PDF. Receives Highcharts format string. */
  onExportChart: (type: 'image/png' | 'application/pdf') => void;
  /** Called when user picks CSV. Parent owns the data, toolbar owns the trigger. */
  onExportCsv: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const MasterGanttToolbar: React.FC<MasterGanttToolbarProps> = ({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  onApply,
  onRefresh,
  loading,
  onRangePreset,
  groupBy,
  onGroupByChange,
  filterState,
  machines,
  hasActiveFilters,
  onFilterChange,
  onClearFilters,
  multiSelectOn,
  selectedCount,
  selectedKeys,
  onMultiSelectToggle,
  ganttData,
  onBulkCommitSuccess,
  onBulkError,
  onExportChart,
  onExportCsv,
}) => {
  const theme = useTheme();

  // ── Export menu state ─────────────────────────────────────────────────────
  const [exportAnchor, setExportAnchor] = useState<null | HTMLElement>(null);
  const exportMenuOpen = Boolean(exportAnchor);

  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setExportAnchor(event.currentTarget);
  };
  const handleExportClose = () => setExportAnchor(null);

  const handlePng = () => {
    handleExportClose();
    onExportChart('image/png');
  };

  const handlePdf = () => {
    handleExportClose();
    onExportChart('application/pdf');
  };

  const handleCsv = () => {
    handleExportClose();
    onExportCsv();
  };

  // ── Adapt MasterGanttFilterState to MachineScheduleFilterState shape ─────
  // MachineScheduleFilters expects a state with `showTentative` (not `showProposals`).
  // FEAT-2026-0007 6E.1: quoteKey / workOrderKey removed from MachineScheduleFilterState,
  // so the bridge no longer includes them (Master Gantt's own URL-sticky filter
  // state still carries them for server-side filtering via useMasterGanttFilters).
  // BUG-2026-0080: memoise so the prop identity is stable across renders that
  // don't change a filter value.
  const bridgedFilterState = React.useMemo(() => ({
    labSections:   filterState.labSections,
    machineCodes:  filterState.machineCodes,
    workTypes:     filterState.workTypes,
    showTentative: filterState.showProposals,
    q:             '',
  }), [
    filterState.labSections,
    filterState.machineCodes,
    filterState.workTypes,
    filterState.showProposals,
  ]);

  // The bridge from MachineScheduleFilters' `onFilterChange` back to our state.
  // BUG-2026-0080: wrap in useCallback so the callback identity is stable
  // across renders.
  const handleBridgedFilterChange = React.useCallback((
    patch: Partial<typeof bridgedFilterState>,
  ) => {
    const masterPatch: Partial<MasterGanttFilterState> = {};
    if ('labSections'   in patch) masterPatch.labSections  = patch.labSections;
    if ('machineCodes'  in patch) masterPatch.machineCodes = patch.machineCodes;
    if ('workTypes'     in patch) masterPatch.workTypes     = patch.workTypes;
    // `showTentative` in the bridge maps to `showProposals` in master state
    if ('showTentative' in patch) masterPatch.showProposals = patch.showTentative;
    onFilterChange(masterPatch);
  }, [onFilterChange]);

  // ── Quick-range preset dropdown ───────────────────────────────────────────
  // Which preset (if any) the current window matches — drives the dropdown
  // value; null = a custom range typed into the pickers.
  const activePreset = React.useMemo(
    () => matchRangePreset(startDate, endDate, new Date()),
    [startDate, endDate],
  );
  const handleRangePresetChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const preset = SCHEDULE_RANGE_PRESETS.find((p) => p.key === event.target.value);
    if (!preset) return;
    const { startDate: s, endDate: e } = preset.compute(new Date());
    onRangePreset(s, e);
  };

  return (
    <Box
      component="header"
      sx={{
        borderBottom: `1px solid ${theme.border.color}`,
        bgcolor: theme.palette.background.paper,
        flexShrink: 0,
      }}
    >
      {/* ── Row 1: date range + toggles + export ─────────────────────────── */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
          minHeight: 52,
        }}
      >
        <Typography variant="h6" fontWeight={600} sx={{ mr: 0.5, flexShrink: 0 }}>
          Master View
        </Typography>

        <Divider orientation="vertical" flexItem />

        {/* Quick-range preset dropdown (sets both dates + applies) */}
        <TextField
          select
          label="Quick range"
          size="small"
          value={activePreset ?? 'custom'}
          onChange={handleRangePresetChange}
          InputLabelProps={{ shrink: true }}
          sx={{ width: 170, flexShrink: 0 }}
          inputProps={{ 'aria-label': 'Quick date range for Master Gantt' }}
        >
          {activePreset === null && (
            <MenuItem value="custom" disabled>
              Custom range
            </MenuItem>
          )}
          {SCHEDULE_RANGE_PRESETS.map((p) => (
            <MenuItem key={p.key} value={p.key}>
              {p.label}
            </MenuItem>
          ))}
        </TextField>

        {/* Date range pickers */}
        <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
          <TextField
            type="date"
            label="Start date"
            size="small"
            value={startDate}
            onChange={(e) => onStartDateChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'aria-label': 'Start date for Master Gantt' }}
            sx={{ width: 160 }}
          />
          <Typography variant="body2" color="text.secondary">
            to
          </Typography>
          <TextField
            type="date"
            label="End date"
            size="small"
            value={endDate}
            onChange={(e) => onEndDateChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ 'aria-label': 'End date for Master Gantt' }}
            sx={{ width: 160 }}
          />
        </Stack>

        <Button
          variant="contained"
          size="small"
          onClick={onApply}
          disabled={loading}
          sx={{ minWidth: 80, flexShrink: 0 }}
        >
          {loading ? <CircularProgress size={14} color="inherit" /> : 'Apply'}
        </Button>

        <Tooltip title="Refresh">
          <span>
            <IconButton
              size="small"
              onClick={onRefresh}
              disabled={loading}
              aria-label="Refresh Master Gantt"
            >
              <Refresh fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        {/* Flexible spacer */}
        <Box sx={{ flex: 1 }} />

        {/* ── Group by toggle (REQ-03) ─────────────────────────────────────── */}
        <Stack direction="row" spacing={0.75} alignItems="center" flexShrink={0}>
          <Typography
            variant="caption"
            fontWeight={600}
            color="text.secondary"
            sx={{ whiteSpace: 'nowrap' }}
          >
            Group by
          </Typography>
          <ToggleButtonGroup
            value={groupBy}
            exclusive
            size="small"
            onChange={(_e, value: GanttGroupBy | null) => {
              // ToggleButtonGroup fires null when the active button is re-
              // clicked; ignore that so a mode is always selected.
              if (value) onGroupByChange(value);
            }}
            aria-label="Group Master Gantt by"
            sx={{
              '& .MuiToggleButton-root': {
                px: 1,
                py: 0.5,
                textTransform: 'none',
                fontSize: '0.75rem',
                fontWeight: 600,
                border: `1px solid ${theme.border.color}`,
                '&.Mui-selected': {
                  backgroundColor: `${theme.palette.primary.main}18`,
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                },
              },
            }}
          >
            <ToggleButton value="project" aria-label="Group by project">
              <ViewListOutlined fontSize="small" sx={{ mr: 0.5 }} />
              Project
            </ToggleButton>
            <ToggleButton value="machine" aria-label="Group by machine">
              <PrecisionManufacturing fontSize="small" sx={{ mr: 0.5 }} />
              Machine
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* ── Toggle Proposals switch ─────────────────────────────────────── */}
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={filterState.showProposals}
              onChange={(e) =>
                onFilterChange({ showProposals: e.target.checked })
              }
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: theme.schedule.tentative,
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: theme.schedule.tentative,
                },
              }}
            />
          }
          label={
            <Typography
              variant="caption"
              fontWeight={600}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Toggle Proposals
            </Typography>
          }
          sx={{ ml: 0, mr: 0 }}
        />

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* ── Multi-select toggle + bulk actions ────────────────────────── */}
        <Tooltip
          title={
            multiSelectOn
              ? 'Click to exit multi-select mode'
              : 'Enable multi-select — click bars to add to selection'
          }
        >
          <ToggleButton
            value="multiSelect"
            selected={multiSelectOn}
            onChange={() => onMultiSelectToggle(!multiSelectOn)}
            size="small"
            aria-label="Toggle multi-select mode"
            sx={{
              px: 1,
              py: 0.5,
              textTransform: 'none',
              fontSize: '0.75rem',
              fontWeight: 600,
              borderRadius: '6px',
              border: `1px solid ${theme.border.color}`,
              '&.Mui-selected': {
                backgroundColor: `${theme.palette.primary.main}18`,
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
              },
            }}
          >
            <Checklist fontSize="small" sx={{ mr: 0.5 }} />
            Multi-select
          </ToggleButton>
        </Tooltip>

        {/* Selection chip */}
        {multiSelectOn && selectedCount > 0 && (
          <Chip
            label={`${selectedCount} selected`}
            size="small"
            color="primary"
            sx={{
              height: 20,
              fontSize: '0.68rem',
              fontWeight: 700,
              '& .MuiChip-label': { px: 1 },
            }}
          />
        )}

        {/* Bulk action buttons — appear when selection is non-empty */}
        {multiSelectOn && selectedCount > 0 && (
          <MasterGanttBulkActions
            selectedKeys={selectedKeys}
            ganttData={ganttData}
            onCommitSuccess={onBulkCommitSuccess}
            onError={onBulkError}
          />
        )}

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        {/* ── Global Export button ────────────────────────────────────────── */}
        <Button
          variant="outlined"
          size="small"
          startIcon={<FileDownload fontSize="small" />}
          onClick={handleExportClick}
          aria-controls={exportMenuOpen ? 'gantt-export-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={exportMenuOpen ? 'true' : undefined}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.75rem',
            borderRadius: '6px',
            flexShrink: 0,
          }}
        >
          Global Export
        </Button>
        <Menu
          id="gantt-export-menu"
          anchorEl={exportAnchor}
          open={exportMenuOpen}
          onClose={handleExportClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            elevation: 3,
            sx: { minWidth: 160, mt: 0.5 },
          }}
        >
          <MenuItem onClick={handlePng} dense>
            <ListItemIcon>
              <ImageOutlined fontSize="small" />
            </ListItemIcon>
            PNG Image
          </MenuItem>
          <MenuItem onClick={handlePdf} dense>
            <ListItemIcon>
              <PictureAsPdfOutlined fontSize="small" />
            </ListItemIcon>
            PDF Document
          </MenuItem>
          <MenuItem onClick={handleCsv} dense>
            <ListItemIcon>
              <TableChartOutlined fontSize="small" />
            </ListItemIcon>
            CSV Spreadsheet
          </MenuItem>
        </Menu>
      </Box>

      {/* ── Row 2: filter dropdowns ────────────────────────────────────────── */}
      <Box
        sx={{
          px: 2,
          pt: 0.75,
          pb: 1,
          borderTop: `1px solid ${theme.border.color}`,
        }}
      >
        <MachineScheduleFilters
          filterState={bridgedFilterState}
          machines={machines}
          hasActiveFilters={hasActiveFilters}
          onFilterChange={handleBridgedFilterChange}
          onClearFilters={onClearFilters}
        />
      </Box>
    </Box>
  );
};
